import Link from 'next/link';
import SafeImage from '@/components/ui/SafeImage';
import BrandSafeText from '@/components/landing/BrandSafeText';

/**
 * A single blog card (Server Component — plain markup, crawlable <a>).
 *
 * The card deliberately does NOT render `coverImageUrl`: those are the
 * server-generated OG images with the post title baked into the artwork, so
 * showing them above the text title printed the same headline twice per
 * card. Instead every card gets a BRAND cover — a deterministic navy→blue
 * gradient keyed to the post category, with a ghosted K-mark monogram — so
 * the index reads as one visual system. The baked OG images still serve
 * social shares and the article page.
 *
 * `featured` renders the horizontal hero variant used for the newest post.
 */
const fmtDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

// Brand-family cover gradients (navy/blue only — accent-locked). The
// category hash picks one deterministically so a category always keeps
// its look, but the grid still has tonal variety.
const COVERS = [
  'linear-gradient(135deg, #16203F 0%, #0F1E61 55%, #1B35AC 130%)',
  'linear-gradient(135deg, #0F1E61 0%, #152984 60%, #3D5EE1 140%)',
  'linear-gradient(135deg, #13267B 0%, #1B35AC 60%, #0F1E61 140%)',
  'linear-gradient(135deg, #1A2350 0%, #0F1E61 45%, #4360E2 125%)',
];
const coverFor = (category = '') => {
  let h = 0;
  for (let i = 0; i < category.length; i += 1) h = (h * 31 + category.charCodeAt(i)) >>> 0;
  return COVERS[h % COVERS.length];
};

const Cover = ({ post, tall = false }) => {
  const box = tall ? 'h-full min-h-[240px]' : 'aspect-[40/21]';

  // Real banner image (1200×630). object-contain on a matching-ratio box =
  // full fill with ZERO crop; on the taller featured box it centres with
  // dark bars that blend into the banner's own dark background.
  if (post.coverImageUrl) {
    return (
      <div
        className={`relative flex w-full items-center justify-center overflow-hidden bg-[#0b1220] ${box}`}
      >
        <SafeImage
          src={post.coverImageUrl}
          alt={post.coverImageAlt || post.title || 'Blog cover'}
          variant="generic"
          loading="lazy"
          className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-[1.02]"
        />
      </div>
    );
  }

  // Fallback for posts with no cover: brand gradient + ghost K monogram.
  return (
    <div
      className={`relative w-full overflow-hidden ${box}`}
      style={{ background: coverFor(post.category) }}
      aria-hidden="true"
    >
      <div
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)',
          backgroundSize: '44px 44px',
        }}
      />
      <div
        className="absolute -bottom-16 -right-10 h-56 w-56 rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(61,94,225,0.35) 0%, transparent 70%)' }}
      />
      <span className="font-sora absolute -bottom-7 right-3 select-none text-[120px] font-black leading-none text-white/[0.08]">
        K
      </span>
      {post.category ? (
        <span className="font-sora absolute left-5 top-5 rounded-full bg-white/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-white/90 backdrop-blur-sm">
          {post.category}
        </span>
      ) : null}
    </div>
  );
};

// #5A6A7A rather than the old #7a8a99: at 12px on white that grey measured
// 3.54:1, under the 4.5:1 WCAG AA requires for body text. This is the same
// hue a step darker, at 5.1:1, so the byline/date row still reads as
// secondary to the title above it without failing the check.
const Meta = ({ post, light = false }) => (
  <div className={`flex items-center gap-2 text-xs ${light ? 'text-white/70' : 'text-[#5A6A7A]'}`}>
    {post.author ? (
      <span className={`font-semibold ${light ? 'text-white/90' : 'text-[#4A5568]'}`}>
        {/* Bylines are usually "Kaympus Team" — brand-locked like the titles,
            or the widget renders it "केम्पस टीम" on one card and
            "कायम्पस टीम" on the next. */}
        <BrandSafeText>{post.author}</BrandSafeText>
      </span>
    ) : null}
    {post.author && post.publishedAt ? <span aria-hidden>·</span> : null}
    {post.publishedAt ? (
      <time dateTime={new Date(post.publishedAt).toISOString()}>{fmtDate(post.publishedAt)}</time>
    ) : null}
    {post.readingTimeMins ? (
      <>
        <span aria-hidden>·</span>
        <span>{post.readingTimeMins} min read</span>
      </>
    ) : null}
  </div>
);

export default function BlogCard({ post, featured = false }) {
  if (!post?.slug) return null;
  const href = `/blog/${post.slug}`;

  if (featured) {
    return (
      <article
        className="group overflow-hidden rounded-[28px] border border-[#3D5EE1]/15 bg-white shadow-[0_10px_40px_rgba(61,94,225,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_56px_rgba(61,94,225,0.14)]"
        data-testid={`blog-card-${post.slug}`}
      >
        {/* Banner on top at its exact 40:21 ratio → no crop, no bars,
            consistent with the grid cards + detail page. */}
        {/* Hidden from assistive tech and taken out of the tab order, rather
            than given an aria-label. It points at exactly the same href as the
            title link a few lines below, so as a real link it was a duplicate:
            a screen reader announced every card twice and keyboard users had
            to tab past a nameless thumbnail to reach the title. It also failed
            `label-content-name-mismatch` — the cover's own text was not part
            of the aria-label that replaced it.

            `tabIndex={-1}` is not optional here: `aria-hidden` on something
            still focusable is its own violation (a keyboard user lands on an
            element the screen reader refuses to describe). Together they make
            the cover exactly what it is — decoration that happens to be
            clickable for mouse users. */}
        <Link href={href} className="block" aria-hidden="true" tabIndex={-1}>
          <Cover post={post} />
        </Link>
        <div className="flex flex-col gap-4 p-7 md:p-10">
          <span className="font-sora w-fit rounded-full bg-[#3D5EE1]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-[#1B35AC]">
            Latest
          </span>
          <h2 className="font-sora text-[24px] font-extrabold leading-snug tracking-tight text-[#0d1117] md:text-[30px]">
            <Link href={href} className="transition-colors hover:text-[#3D5EE1]">
              <BrandSafeText>{post.title}</BrandSafeText>
            </Link>
          </h2>
          {post.excerpt ? (
            <p className="line-clamp-3 text-[15px] leading-relaxed text-[#596875]">
              <BrandSafeText>{post.excerpt}</BrandSafeText>
            </p>
          ) : null}
          <Meta post={post} />
        </div>
      </article>
    );
  }

  return (
    <article
      className="group flex flex-col overflow-hidden rounded-[22px] border border-[#3D5EE1]/12 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_44px_rgba(61,94,225,0.12)]"
      data-testid={`blog-card-${post.slug}`}
    >
      {/* Decorative duplicate of the title link below — hidden from assistive
          tech and out of the tab order. Same reasoning as the featured
          variant above. */}
      <Link href={href} className="block" aria-hidden="true" tabIndex={-1}>
        <Cover post={post} />
      </Link>
      <div className="flex flex-1 flex-col p-6">
        <h2 className="font-sora text-[17px] font-bold leading-snug tracking-tight text-[#0d1117]">
          <Link href={href} className="transition-colors hover:text-[#3D5EE1]">
            <BrandSafeText>{post.title}</BrandSafeText>
          </Link>
        </h2>
        {post.excerpt ? (
          <p className="mt-2.5 line-clamp-3 text-sm leading-relaxed text-[#596875]">
            <BrandSafeText>{post.excerpt}</BrandSafeText>
          </p>
        ) : null}
        <div className="mt-auto pt-5">
          <Meta post={post} />
        </div>
      </div>
    </article>
  );
}

